import { memo, useState } from 'react';

import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import { getPQNumericValue } from 'common/util/pqUtil';
import PowerQueryResultTable from 'components/common/PowerQueryResultTable/PowerQueryResultTable';

import * as K from 'common/constants';
import { propsAreEqual } from 'common/util/propsAreEqual';
import { useEventCache } from 'hooks/useLRQ/useEventCache';
import { ErrorResult } from 'hooks/useLRQ/useLRQ';

import { getType, substituteParameters } from 'components/dashboards/dashboardData';
import DashboardNumber from 'components/dashboards/DashboardPage/DashboardNumber';
import { DashboardConfigType, DashboardParam, GraphConfigType } from 'components/dashboards/types';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import NoResults from 'components/dashboards/DashboardPage/NoResults';
import ClearableInput from 'components/common/ClearableInput/ClearableInput';
import DashboardIncompleteResults from 'components/dashboards/DashboardPage/DashboardIncompleteResults';
import DashboardTransposeTable from './DashboardTransposeTable';
import { ErrorEditGraphJSON } from './modals/EditGraphJSON';

import './DashboardPowerQuery.scss';

type DashboardPowerQueryType = {
  config: GraphConfigType;
  index: number;
  dataIds: string[];
  params: Array<DashboardParam>;
  dashboardConfig: DashboardConfigType;
};

/**
 * Crack open an LRQ PQ ErrorResult to obtain the error within. As of 6/2020, power queries
 * return three flavors of user-caused errors, all as ValidationExceptions.
 *
 * @param errorResult A potential LRQ error
 */
export const getRequestErrorMessage = (errorResult: ErrorResult): string => {
  if (errorResult.terminationReasons !== undefined) {
    const reasons = errorResult.terminationReasons as Array<string>;
    if (reasons?.length > 0 && reasons[0] === 'ValidationException') {
      if (errorResult.terminationReasonsDetails !== undefined) {
        const details = (errorResult.terminationReasonsDetails as Record<string, string>[][])[0];
        for (let index = 0; index < details.length; index += 1) {
          if (details[index]?.field === K.FIELD.MESSAGE) {
            return `Syntax error: ${details[index].message}`;
          }
        }
      }
      return reasons[0];
    }
  }
  return 'An unknown error has occurred.';
};

/**
 * Table, pie chart or big number display for a dashboard.
 *
 * The type-specific display components do all the heavy-lifting -- this component just wraps them.
 *
 * @prop index                  The index of this chart within the dashbaord config
 * @prop config                 The config stanza for just this power query
 * @prop dataIds                The list of LRQ data ids to retreive data (only one for a power query)
 * @prop isDarkMode             true if dark mode, false otherwise
 * @prop windowWidth            The overall window width
 * @prop chartInstanceCallback  A callback to capture the Highcharts chart instance (if any) -- used for resizing
 * @prop dashboardConfig        The config for the dashboard
 */
function DashboardPowerQuery({
  index,
  config,
  dataIds,
  params,
  dashboardConfig,
}: DashboardPowerQueryType): JSX.Element {
  const [filterText, setFilterText] = useState('');
  const vizType = getType(config);

  // --------------------------------------------------------------------------------------------------------------- ERRORS FIRST
  const { results, errors } = useEventCache(dataIds);

  if (errors?.length > 0 && errors[0]) {
    const message = getRequestErrorMessage(errors[0]);
    const isSyntaxError = message.toLowerCase().includes('syntax');
    if (isSyntaxError) {
      return <ErrorEditGraphJSON message={message} graphIndex={index} graphConfig={config} />;
    }
    return <ErrorPlaceholder error={{ message }} />;
  }

  // --------------------------------------------------------------------------------------------------------------- NO DATA YET
  // You could get each of these stages before the query is ready
  if (!results || results.length === 0 || !results[0].pqResultData || !results[0].pqResultData.values) {
    return <LoadingPlaceholder offsetTop={0} />;
  }

  // --------------------------------------------------------------------------------------------------------------- NO RESULTS
  const pqResultData = results[0].pqResultData;
  const rowValues = pqResultData.cells || pqResultData.values;

  // If no data for built-in log volume dashboard reports, show "No Kubernetes logs" error
  if (dashboardConfig.isBuiltIn && dashboardConfig.name.toLowerCase() === 'log volume' && !rowValues.length) {
    return <NoResults config={config} params={params} message="No Kubernetes Logs" />;
  }

  if (!rowValues.length) {
    return <NoResults config={config} params={params} />;
  }

  const columns = pqResultData.columns;
  // Extract column data for a specific type of column values
  const findColumn = (colType: string): number => columns.findIndex((column) => column.format === colType);

  const matchCount = pqResultData?.matchCount;
  const omittedEvents = pqResultData?.omittedEvents;
  const partialResultsDueToTimeLimit = pqResultData?.partialResultsDueToTimeLimit;

  // --------------------------------------------------------------------------------------------------------------- BIG NUMBER DISPLAY
  if (vizType.isNumber && rowValues) {
    // We can't be sure what the shape of the data is, so try to find a number in it to show, and let component deal with the results
    const valueCol = findColumn('number');
    const numberValue = valueCol > -1 ? getPQNumericValue(rowValues[0][valueCol]) : undefined;
    const linkValue = config.query
      ? '/query?filter=' + encodeURI(substituteParameters(params, config.query as string))
      : '#';
    return <DashboardNumber config={config} linkValue={linkValue} numberValue={numberValue} />;
  }

  const tableDisplayOptions = {
    hoverRow: false,
    sidePadding: 15,
    size: K.SIZE.MEDIUM,
    isStriped: false,
    isSortable: true,
    rowClass: 'DashboardPowerQuery__TableRow',
    itemClass: 'DashboardPowerQuery__TableItem',
    headerItemClass: 'DashboardPowerQuery__TableHeaderItem',
    footerClass: 'DashboardPowerQuery__TableFooter',
    showBarsColumn: config.showBarsColumn,
  };

  return (
    <>
      <div className="DashboardGraph__Content DashboardPowerQuery">
        <DashboardIncompleteResults
          matchCount={matchCount}
          omittedEvents={omittedEvents}
          partialResultsDueToTimeLimit={partialResultsDueToTimeLimit}
          rowCount={rowValues.length}
        />
        {rowValues.length > 0 ? (
          // show single column view table in widget when the transposeTable value is true and has a single row count
          config.transposeTable === 'true' && rowValues.length === 1 ? (
            <>
              <DashboardTransposeTable columns={pqResultData.columns} rowValues={rowValues[0]} />
            </>
          ) : (
            <>
              <ClearableInput
                placeholder="Filter results"
                onChange={(text): void => setFilterText(text.toLowerCase())}
                value={filterText}
              />
              <div className="DashboardPowerQuery__Holder">
                <PowerQueryResultTable
                  headers={pqResultData.columns}
                  isNonVirtual
                  filterText={filterText}
                  rowValues={rowValues}
                  displayOptions={tableDisplayOptions}
                  dashboardName={dashboardConfig.name}
                />
              </div>
            </>
          )
        ) : (
          <></>
        )}
      </div>
    </>
  );
}

// This component has very few properties that change => make sure no unnecessary renders happen
export default memo(DashboardPowerQuery, propsAreEqual);
